/**
 * LogicSetInfoModal
 * PBI 266671 - Logic Set dynamic palette information
 *
 * Modal that displays an interactive Logic Set diagram with
 * dynamic explanatory text for each element.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, shallowEqual } from 'react-redux'
// Modal import removed - using StyledModal from style file
import strings from 'utils/strings'
import { opMap } from 'components/utils'
import { mergedConditionSelector } from 'selectors/feature/conditions'
import {
  generateLogicGateText,
  generateActivateDelayText,
  generateDeactivateDelayText,
  getActiveElements,
  getEdgeTriggerText,
} from 'utils/logicSetTextGenerator'
import { translationValue } from 'utils/translations'
import { timerValue } from 'utils/timers'
import { truncateText } from 'utils/utils'
import { Icon__Info as InfoIcon } from 'components/patterns/Icon/components'
import Button from 'components/patterns/Button'
import {
  CARRY_LOGIC_SET,
  CARRY_TIMER,
  SELF_REFERENCE,
  ANALOG_INPUT,
  ANALOG_CONDITION,
  DIGITAL_INPUT,
  DIGITAL_CONDITION,
} from 'utils/constants'
import {
  formatAnalogInputPinLabel,
  formatDigitalInputPinLabel,
} from 'components/feature/utils/conditionLabelText'
import { getSwitchPositionLabel } from 'components/feature/utils'
import LogicSetDiagramReadOnly from './LogicSetDiagramReadOnly'

import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  InstructionText,
  ModalContent,
  DiagramPanel,
  TilesPanel,
  Tile,
  TileHeader,
  TileLabel,
  TileDescription,
} from './LogicSetInfoModal.style'

/**
 * Helper function to render text with line breaks
 * Converts \n characters to <br /> elements for proper HTML rendering
 * @param {string} text - Text that may contain \n characters
 * @returns {React.ReactNode} - Text with <br /> elements
 */
const renderTextWithLineBreaks = (text) => {
  if (!text || typeof text !== 'string') return text
  const parts = text.split('\n')
  if (parts.length === 1) return text
  return parts.map((part, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 && <br />}
    </React.Fragment>
  ))
}

/**
 * Extract condition details from merged condition data
 * @param {Object} conditionData - The merged condition data (plain JS object)
 * @returns {Object|null} - Extracted details or null
 */
const extractConditionDetails = (conditionData) => {
  if (!conditionData) return null
  return {
    selectedInputDescription: conditionData.selectedInputDescription,
    customDescription: conditionData.customDescription || null,
    inputUri: conditionData.inputUri || null,
    uriOptionsByPosition: conditionData.uriOptionsByPosition || [],
    operator: conditionData.operator,
    value: conditionData.value,
    result: conditionData.result,
    rungType: conditionData.rungType,
    isCarry:
      conditionData.rungType === CARRY_LOGIC_SET ||
      conditionData.rungType === CARRY_TIMER ||
      conditionData.rungType === SELF_REFERENCE,
    translationType: conditionData.translationType,
    translations: conditionData.translations || [],
    edgeTriggerType: conditionData.edge || 'none',
    hysteresis: conditionData.hysteresis,
    units: conditionData.units,
  }
}

/**
 * Resolves the display name for a condition's input signal.
 * Priority: user-assigned custom description > formatted pin label > raw description.
 * @param {Object} conditionData - Extracted condition details
 * @returns {string} - The display name to use in explanation text
 */
export const resolveInputDisplayName = ({
  customDescription,
  selectedInputDescription,
  rungType,
  inputUri,
  uriOptionsByPosition,
}) => {
  if (customDescription) {
    const position = getSwitchPositionLabel(inputUri, uriOptionsByPosition)
    return position ? `${customDescription} ${position}` : customDescription
  }
  if (rungType === ANALOG_INPUT || rungType === ANALOG_CONDITION) {
    return (
      formatAnalogInputPinLabel(selectedInputDescription) ||
      selectedInputDescription
    )
  }
  if (rungType === DIGITAL_INPUT || rungType === DIGITAL_CONDITION) {
    return (
      formatDigitalInputPinLabel(selectedInputDescription) ||
      selectedInputDescription
    )
  }
  return selectedInputDescription
}

/**
 * Hook to get raw condition data from Redux state
 * Uses targeted selectors to avoid re-renders on unrelated state changes
 */
const useConditionDetails = (conditionUris) => {
  // Select only the specific condition data we need, with shallow equality check
  const conditionDetails = useSelector((state) => {
    const details = {}
    conditionUris.forEach((uri) => {
      if (!uri) return
      const mergedCondition = mergedConditionSelector(state, { queryUri: uri })
      if (!mergedCondition || mergedCondition.size === 0) return
      const conditionData = mergedCondition.toJS
        ? mergedCondition.toJS()
        : mergedCondition
      details[uri] = extractConditionDetails(conditionData)
    })
    return details
  }, shallowEqual)

  return conditionDetails
}

const LogicSetInfoModal = ({
  visible,
  onClose,
  logicSetName,
  conditions,
  abComplexQueryOperator,
  cdComplexQueryOperator,
  finalComplexQueryOperator,
  delayActivate,
  delayDeactivate,
}) => {
  // Track which element is selected (for bi-directional highlighting)
  const [selectedElement, setSelectedElement] = useState(null)
  const [hoveredElement, setHoveredElement] = useState(null)

  // Refs for tile elements to enable auto-scroll
  const tileRefs = useRef({})

  // Auto-scroll to selected tile when selection changes
  useEffect(() => {
    if (selectedElement && tileRefs.current[selectedElement]) {
      tileRefs.current[selectedElement].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [selectedElement])

  // Fetch condition details from Redux
  const conditionDetails = useConditionDetails(conditions)

  // Helper to check if a condition has content (input selected)
  const hasConditionContent = useCallback(
    (index) => {
      const uri = conditions[index]
      if (!uri) return false
      const details = conditionDetails[uri]
      return details && details.selectedInputDescription
    },
    [conditions, conditionDetails]
  )

  // Get the list of active elements (tiles to display)
  // Filter out empty conditions (no input selected)
  const activeElements = useMemo(() => {
    const elements = getActiveElements({
      conditions,
      delayActivate,
      delayDeactivate,
    })

    return elements.filter((element) => {
      // For conditions, only show if they have content
      if (element.type === 'condition') {
        return hasConditionContent(element.index)
      }
      // For logic gates, check if related conditions have content
      if (element.type === 'logicGate') {
        if (element.position === 'top') {
          // Top gate needs at least one of conditions 0 or 1 with content
          return hasConditionContent(0) || hasConditionContent(1)
        }
        if (element.position === 'bottom') {
          // Bottom gate needs at least one of conditions 2 or 3 with content
          return hasConditionContent(2) || hasConditionContent(3)
        }
        if (element.position === 'middle') {
          // Middle gate needs at least one condition with content
          return (
            hasConditionContent(0) ||
            hasConditionContent(1) ||
            hasConditionContent(2) ||
            hasConditionContent(3)
          )
        }
      }
      // Delays are always shown if present
      return true
    })
  }, [conditions, delayActivate, delayDeactivate, hasConditionContent])

  // Generate text for each element
  const getElementText = useCallback(
    (element) => {
      if (element.type === 'condition') {
        const conditionData = conditionDetails[conditions[element.index]]
        if (!conditionData) return ''

        const {
          selectedInputDescription,
          operator,
          value,
          isCarry,
          translationType,
          translations,
          edgeTriggerType,
          hysteresis,
          units,
        } = conditionData

        // If no input is selected, show empty description
        if (!selectedInputDescription) {
          return ''
        }

        // Get translated value (e.g., "1" -> "Pressed"), fallback to raw value
        const translatedValue =
          translationValue(value, translationType, translations) || value

        // Use custom name when set; fall back to formatted pin label or raw description
        const inputDescription = resolveInputDisplayName(conditionData)

        // For carry conditions
        if (isCarry) {
          const { rungType } = conditionData
          // For Carry Timer, show the operator and formatted timer value
          if (rungType === CARRY_TIMER) {
            const numOp = parseInt(operator, 10)
            const operatorText = opMap[numOp] || opMap[operator] || operator
            const formattedTimerValue = timerValue(value)
            return `Is TRUE when ${inputDescription}'s value ${operatorText} ${formattedTimerValue}.`
          }
          // For Carry Logic Set / Self Reference, show TRUE/FALSE
          // Use the configured 'value' field (not 'result' which is live evaluation)
          const carryValue =
            value === 1 || value === '1' || value === true ? 'TRUE' : 'FALSE'
          return `Is TRUE when ${inputDescription} is ${carryValue}.`
        }

        // Map edge values from storage format to expected format
        // Storage: 'NoEdge', 'RisingEdge', 'FallingEdge', 'BothEdges'
        // Expected: 'none', 'rising', 'falling', 'both'
        const edgeMap = {
          NoEdge: 'none',
          RisingEdge: 'rising',
          FallingEdge: 'falling',
          BothEdges: 'both',
        }
        const normalizedEdge = edgeMap[edgeTriggerType] || 'none'

        // Get edge trigger text suffix
        const edgeText = getEdgeTriggerText(normalizedEdge)

        // For normal conditions, build the condition text
        const numOperator = parseInt(operator, 10)
        const operatorText = opMap[numOperator] || opMap[operator] || operator
        const unitSuffix = units ? ` ${units}` : ''
        const hysteresisOperators = [4, 5, 6, 7]
        const hasHysteresis =
          hysteresis != null &&
          hysteresis !== 0 &&
          hysteresisOperators.includes(numOperator)

        if (hasHysteresis) {
          // Build full hysteresis text with complementary operator and release value
          // Operator 5 (<) / 7 (≤): complementary is ≥ / >, release = value + hysteresis
          // Operator 4 (>) / 6 (≥): complementary is ≤ / <, release = value - hysteresis
          const complementaryOpMap = { 5: '≥', 7: '>', 4: '≤', 6: '<' }
          const addOps = [5, 7]
          const complementaryOp = complementaryOpMap[numOperator]
          const releaseValue = addOps.includes(numOperator)
            ? Number(value) + Number(hysteresis)
            : Number(value) - Number(hysteresis)
          const hysteresisClause = `and once met, remains TRUE until the ${inputDescription} ${complementaryOp} ${releaseValue}${unitSuffix}`

          if (normalizedEdge !== 'none') {
            const conditionClause = `${inputDescription} ${operatorText} ${translatedValue}${unitSuffix}`
            return `Is TRUE when ${conditionClause}${edgeText} and when ${conditionClause} ${hysteresisClause}. Combining an Edge Trigger with Hysteresis may produce unexpected results.`
          }

          return `Is TRUE when ${inputDescription} ${operatorText} ${translatedValue}${unitSuffix} ${hysteresisClause}.`
        }

        return `Is TRUE when ${inputDescription} ${operatorText} ${translatedValue}${unitSuffix}${edgeText}.`
      }

      if (element.type === 'logicGate') {
        if (element.position === 'top') {
          return `${generateLogicGateText(
            abComplexQueryOperator,
            strings.condition_number_label(1),
            strings.condition_number_label(2)
          )}.`
        }
        if (element.position === 'bottom') {
          return `${generateLogicGateText(
            cdComplexQueryOperator,
            strings.condition_number_label(3),
            strings.condition_number_label(4)
          )}.`
        }
        if (element.position === 'middle') {
          if (finalComplexQueryOperator?.toLowerCase() === 'or') {
            return `${strings.logic_gate_middle_or_text(
              strings.logic_gate_top,
              strings.logic_gate_bottom
            )}.`
          }
          return `${generateLogicGateText(
            finalComplexQueryOperator,
            strings.logic_gate_top,
            strings.logic_gate_bottom
          )}.`
        }
      }

      if (element.type === 'delay') {
        if (element.delayType === 'activate') {
          return `${generateActivateDelayText(delayActivate)}.`
        }
        if (element.delayType === 'deactivate') {
          return `${generateDeactivateDelayText(delayDeactivate)}.`
        }
      }

      return ''
    },
    [
      conditions,
      conditionDetails,
      abComplexQueryOperator,
      cdComplexQueryOperator,
      finalComplexQueryOperator,
      delayActivate,
      delayDeactivate,
    ]
  )

  // Create element ID for selection matching
  const getElementId = (element) => {
    if (element.type === 'condition') return `condition-${element.index}`
    if (element.type === 'logicGate') return `gate-${element.position}`
    if (element.type === 'delay') return `delay-${element.delayType}`
    return ''
  }

  // Handle element selection (from diagram or tile)
  const handleSelect = useCallback((elementId) => {
    setSelectedElement((prev) => (prev === elementId ? null : elementId))
  }, [])

  // Handle hover events
  const handleHover = useCallback((elementId) => {
    setHoveredElement(elementId)
  }, [])

  const handleLeave = useCallback(() => {
    setHoveredElement(null)
  }, [])

  // Store ref for a tile element
  const setTileRef = useCallback((elementId, el) => {
    tileRefs.current[elementId] = el
  }, [])

  // Check if an element is selected
  const isSelected = useCallback(
    (elementId) => selectedElement === elementId,
    [selectedElement]
  )

  // Render a tile in the right panel
  const renderTile = useCallback(
    (element) => {
      const elementId = getElementId(element)
      const description = getElementText(element)

      return (
        <Tile
          key={elementId}
          ref={(el) => setTileRef(elementId, el)}
          isSelected={isSelected(elementId)}
          onClick={() => handleSelect(elementId)}
          onMouseEnter={() => handleHover(elementId)}
          onMouseLeave={handleLeave}
        >
          <TileHeader>
            <TileLabel>{element.label}</TileLabel>
          </TileHeader>
          <TileDescription>
            {renderTextWithLineBreaks(description)}
          </TileDescription>
        </Tile>
      )
    },
    [
      getElementText,
      handleHover,
      handleLeave,
      handleSelect,
      isSelected,
      setTileRef,
    ]
  )

  return (
    <StyledModal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
      maskClosable={false}
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {strings.learn_how_logic_set_title(truncateText(logicSetName, 40))}
          </ModalTitle>
        </ModalHeader>

        <ModalContent>
          <DiagramPanel>
            <InstructionText>
              <InfoIcon
                width={16}
                height={16}
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              />
              {strings.select_element_for_details}
            </InstructionText>
            <LogicSetDiagramReadOnly
              logicSetName={logicSetName}
              conditions={conditions}
              abComplexQueryOperator={abComplexQueryOperator}
              cdComplexQueryOperator={cdComplexQueryOperator}
              finalComplexQueryOperator={finalComplexQueryOperator}
              delayActivate={delayActivate}
              delayDeactivate={delayDeactivate}
              selectedElement={selectedElement}
              hoveredElement={hoveredElement}
              onSelectElement={handleSelect}
              onHoverElement={setHoveredElement}
              onLeaveElement={() => setHoveredElement(null)}
            />
          </DiagramPanel>

          <TilesPanel>
            {activeElements.map((element) => renderTile(element))}
          </TilesPanel>
        </ModalContent>

        <ModalFooter>
          <Button size="large" doMargins={false} onClick={onClose}>
            {strings.close || 'Close'}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </StyledModal>
  )
}

LogicSetInfoModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  logicSetName: PropTypes.string.isRequired,
  conditions: PropTypes.arrayOf(PropTypes.string),
  abComplexQueryOperator: PropTypes.string,
  cdComplexQueryOperator: PropTypes.string,
  finalComplexQueryOperator: PropTypes.string,
  delayActivate: PropTypes.number,
  delayDeactivate: PropTypes.number,
}

LogicSetInfoModal.defaultProps = {
  conditions: [],
  abComplexQueryOperator: 'or',
  cdComplexQueryOperator: 'or',
  finalComplexQueryOperator: 'or',
  delayActivate: 0,
  delayDeactivate: 0,
}

export default LogicSetInfoModal
